import React, { Children, useEffect, useState } from 'react'
import { useWindowSize } from '../../hooks/useWindowSize'
import { SetState } from '../../vite-env'
import { SliderContent } from '../Animations/Slider/SliderContent'
import { navigateToSlideIndex } from '../Animations/Slider/slider.helpers'
import { Step, StepProps } from './Step'
import { StepperFooter, StepperHeader } from './components'
import { StepperAccordion } from './components/Header/StepperAccordion'
import { UseFormReturn } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'

export type StepperVariant = 'tabs' | 'accordion'

type Props = (
  | {
      currentStep: number
      setCurrentStep: SetState<number>
    }
  | {
      defaultStep?: number
    }
) & {
  className?: string
  sliderClasses?: string
  children: (React.FunctionComponentElement<StepProps> | false)[] // Children Should have step props
  hideHeader?: boolean
  fullPage?: boolean
  hideFooter?: boolean
  /** Opt-in: pair with `<Stepper.Step fields={[...]}>` to have the
   *  footer auto-gate Next on rhf errors for those field paths and
   *  trigger validation on click. Most consumers should compute
   *  `disabled` per step instead — this is left in for convenience. */
  form?: UseFormReturn<any>
  addMarginAndSoftenEdges?: boolean
  /** Layout of the step header. `tabs` (default) renders a horizontal
   * progress bar across the top; `accordion` renders each step as a
   * vertical accordion item with only the current step expanded. */
  variant?: StepperVariant
  /** Accordion variant only — when true, the user can jump to any
   * step regardless of completion state. Useful for edit flows. */
  allowJumpToAnyStep?: boolean
  /** Accordion variant only — when true, steps that the user has not
   * yet reached are not rendered. Steps reveal one at a time as the
   * user progresses. Going back to a previous step does not unhide
   * already-revealed later steps. */
  hideUntouchedSteps?: boolean
}

export const Stepper = (props: Props) => {
  const controlled = 'currentStep' in props && 'setCurrentStep' in props

  // const internalState = useState((props.defaultStep || 1) - 1)
  const [currentStepIndex, setCurrentStepIndex] = controlled
    ? [props.currentStep, props.setCurrentStep]
    : useState((props.defaultStep || 1) - 1)

  const [stepPropsList, setStepPropsList] = useState<StepProps[]>([])

  const [windowHeight] = useWindowSize()


  const variant: StepperVariant = props.variant ?? 'tabs'

  // Monotonic high-water mark of visited steps. Used by
  // `hideUntouchedSteps` to reveal later steps as the user advances
  // while keeping them visible if the user navigates back.
  const [maxStepReached, setMaxStepReached] = useState(currentStepIndex)
  // A high-water mark is history-dependent, not derivable from the current
  // (controlled) step alone, so it must live in state and sync on advance —
  // useMemo/derivation can't express "the furthest step ever visited".
  useEffect(() => {
    setMaxStepReached((prev) => Math.max(prev, currentStepIndex))
  }, [currentStepIndex])

  useEffect(() => {
    setStepPropsList(
      Children.toArray(props.children)
        .filter((child): child is React.ReactElement<StepProps> =>
          React.isValidElement(child),
        )
        .map((child, i) => ({
          ...child.props,
          completed: i < currentStepIndex,
        })),
    )
  }, [props.children, currentStepIndex])

  const onClickTitle = async (stepIndex: number) => {
    if (stepIndex > currentStepIndex) {
      // Advancing: the leaving step's onNext can veto the jump by returning
      // false (await in case it's async), mirroring the footer Next button.
      const result = await stepPropsList[currentStepIndex]?.onNext?.()
      if (result === false) return
      setStepPropsList((prev) =>
        prev.map((step, i) =>
          i === currentStepIndex ? { ...step, completed: true } : step,
        ),
      )
    }

    setCurrentStepIndex(() =>
      navigateToSlideIndex(stepIndex, currentStepIndex, stepPropsList),
    )
  }

  const isAccordion = variant === 'accordion'

  return (
    <section
      style={
        props.fullPage ? { height: windowHeight, maxHeight: windowHeight } : {}
      }
      className={twMerge(
        `w-full h-full flex flex-col justify-between items-stretch grow basis-0 overflow-clip`,
        props.addMarginAndSoftenEdges && '-m-8 p-8',
        props.className,
      )}>
      {!isAccordion && !props.hideHeader && (
        <StepperHeader
          currentStep={currentStepIndex}
          stepPropsList={stepPropsList}
          onClickTitle={onClickTitle}
        />
      )}

      {isAccordion ? (
        <StepperAccordion
          form={props.form}
          currentStep={currentStepIndex}
          stepPropsList={stepPropsList}
          steps={Children.toArray(props.children)}
          allowJumpToAnyStep={props.allowJumpToAnyStep}
          hideUntouched={props.hideUntouchedSteps}
          maxStepReached={maxStepReached}
          onClickTitle={onClickTitle}
        />
      ) : (
        <SliderContent
          addMarginAndSoftenEdges={props.addMarginAndSoftenEdges}
          selectedSlide={currentStepIndex}
          className={props.sliderClasses}>
          {props.children}
        </SliderContent>
      )}

      {!props.hideFooter && (
        <StepperFooter
          currentStepIndex={currentStepIndex}
          setCurrentStepIndex={setCurrentStepIndex}
          stepProps={stepPropsList}
          form={props.form}
        />
      )}
    </section>
  )
}

Stepper.Step = Step
